import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Card, Row, Col, Button, Spinner } from "react-bootstrap";
import "./CampusDriveForm.css";
import JobDetailsForm from "./JobDetailsForm";
import RequirementForm from "./RequirementForm";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { API_URL } from "../../../config";
import axios from "axios";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { getSalaryLPA } from "../../../utils/helper";

const validationSchemaStep1 = Yup.object({
  companyName: Yup.string().required("Company name is required"),
  jobPostName: Yup.object().required("Job post name is required"),
  jobType: Yup.string().required("Job type is required"),
  salaryStipend: Yup.number()
    .required("Salary/Stipend is required")
    .positive("Must be a positive number")
    .integer("Must be an integer"),
  compensationDetails: Yup.string()
    .notRequired()
    .test(
      "is-valid-length",
      "Must be at least 20 characters",
      (value) => !value || (typeof value === "string" && value.length >= 20)
    ),
  jobLocation: Yup.string().required("Job location is required"),
  jobLocationId: Yup.string().required("Job location is required"),
  // jobLocationId: Yup.string().notRequired(),
  totalVacancy: Yup.number()
    .required("Total vacancy is required")
    .positive("Must be a positive number")
    .integer("Must be an integer"),
  companyAddress: Yup.string().required("Company address is required"),
  companyHRContact: Yup.string()
    .test(
      "is-valid-length",
      "Must be between 10 and 15 digits",
      (value) =>
        !value ||
        (typeof value === "string" && value.length >= 10 && value.length <= 15)
    )
    .required("Company HR contact is required"),
  companyHREmail: Yup.string()
    .email("Invalid email format")
    .required("Company HR email is required"),
  companyHRName: Yup.string().required("Company HR name is required"),
  alternateContactDetails: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "is-valid-length",
      "Must be between 10 and 12 digits",
      (value) =>
        !value ||
        (typeof value === "string" && value.length >= 10 && value.length <= 12)
    ),
  joiningDate: Yup.date().required("Joining date is required"),
  applyOnlineURL: Yup.string().url("Must be a valid URL").notRequired(),
  prePlacementOffer: Yup.boolean(),
  prePlacementOfferEligibilityCriteria: Yup.string().when(
    ["prePlacementOffer", "jobType"],
    {
      is: (prePlacementOffer, jobType) =>
        prePlacementOffer === true && jobType === "internship",
      then: (schema) =>
        schema.required("Pre-Placement Offer eligibility criteria is required"),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
});

const validationSchemaStep2 = Yup.object({
  jobDescription: Yup.string()
    .min(200, "Minimum 200 characters allowed")
    .required("Job description is required"),
  placementEligibilityCriteria: Yup.string()
    .min(100, "Minimum 100 characters allowed")
    .required("Placement eligibility criteria is required"),
  interviewRounds: Yup.string()
    .test(
      "is-valid-length",
      "Must be at least 20 characters",
      (value) => !value || (typeof value === "string" && value.length >= 20)
    )
    .required("Interview rounds are required"),
  termsAndConditions: Yup.string()
    .notRequired()
    .test(
      "is-valid-length",
      "Must be at least 100 characters",
      (value) => !value || (typeof value === "string" && value.length >= 100)
    ),
  selectedCourses: Yup.array()
    .min(1, "Please select at least one course")
    .required("Courses are required"),
  selectedSpecializations: Yup.array().notRequired(),
  selectedPassoutYears: Yup.array()
    .min(1, "Please select at least one passout year")
    .required("Passout years are required"),
  // files: Yup.mixed().when(["job_description_file_name"], {
  //   is: (job_description_file_name) => !job_description_file_name,
  //   then: (schema) => schema.required("Files are required"),
  //   otherwise: (schema) => schema.nullable(),
  // }),
  // additionalFile: Yup.mixed().when(["additional_document_file_name"], {
  //   is: (additional_document_file_name) => !additional_document_file_name,
  //   then: (schema) => schema.required("Additional files are required"),
  //   otherwise: (schema) => schema.nullable(),
  // }),
  // job_description_file_name: Yup.string().required(
  //   "Upload job description file"
  // ),
  // additional_document_file_name: Yup.string().required(
  //   "Upload additional document file"
  // ),
  files: Yup.mixed().notRequired(),
  additionalFile: Yup.mixed().notRequired(),
  job_description_file_name: Yup.string().nullable().notRequired(),
  additional_document_file_name: Yup.string().nullable().notRequired(),
});

const validationSchemaStep3 = Yup.object({
  status: Yup.string().required("Status is required"),
});

const jobTypeOptions = [
  { value: "full-time", label: "Full Time" },
  { value: "part-time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
  { value: "remote", label: "Work From Home" },
];

const CampusDriveForm = ({ drive }) => {
  const [step, setStep] = useState(1);
  const collegeId = useSelector((state) => state.college_id?.college_id);
  const [stepErrors, setStepErrors] = useState({});
  const history = useHistory();
  const [specializations, setSpecializations] = useState([]);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [isAdditionalFileUploaded, setIsAdditionalFileUploaded] =
    useState(false);
  const courses = useSelector((state) => state.courses.courses);
  const initialValues = {
    // Step 1
    companyName: drive?.company_name || "",
    jobPostName: drive
      ? {
        value: drive?.job_role_id,
        label: drive?.job_role_name || drive?.other_job_role_name,
        ...(!drive?.job_role_id && {
          __isNew__: true,
        }),
      }
      : "",
    jobType: drive?.job_type
      ? jobTypeOptions.find((opt) => opt.value === drive?.job_type).value
      : "",
    salaryStipend:
      drive?.salary && !isNaN(drive?.salary) ? parseInt(drive?.salary) : "",
    compensationDetails: drive?.compensation_details || "",
    jobLocation: drive?.job_location || "",
    jobLocationId: drive?.job_location_id || "",
    // jobLocationInitialized: false,
    totalVacancy: drive?.total_vacancy || "",
    companyAddress: drive?.company_address || "",
    companyHRContact: drive?.hr_contact_number || "",
    companyHREmail: drive?.hr_email || "",
    companyHRName: drive?.hr_contact_person || "",
    alternateContactDetails: drive?.alternate_contact_number || "",
    joiningDate:
      drive?.joining_date && moment(drive?.joining_date).isValid()
        ? moment(drive?.joining_date).format("YYYY-MM-DD")
        : "",
    applyOnlineURL: drive?.apply_online_url || "",
    prePlacementOffer: drive?.pre_placement_offer === "Yes" ? true : false,
    prePlacementOfferEligibilityCriteria: drive?.ppo_eligibility_criteria || "",

    // Step 2
    jobDescription: drive?.job_description || "",
    placementEligibilityCriteria: drive?.placement_eligibility_criteria || "",
    interviewRounds: drive?.placement_interview_rounds || "",
    termsAndConditions: drive?.placement_terms_and_conditions || "",
    selectedCourses: drive?.course_id ? drive?.course_id?.split(",") : [],
    selectedSpecializations: drive?.specialization_id
      ? drive?.specialization_id?.split(",")
      : [],
    selectedPassoutYears: drive?.passout_year
      ? drive?.passout_year?.split(",")
      : [],
    files: null,
    additionalFile: null,
    job_description_file_name: drive?.job_description_file_name || "",
    additional_document_file_name: drive?.additional_document_file_name || "",
    ...(drive?.job_description_file_name && {
      job_description_file_url: drive?.job_description_file_url,
    }),
    ...(drive?.additional_document_file_name && {
      additional_document_file_url: drive?.additional_document_file_url,
    }),
    // Step 3
    status: (() => {
      if (drive?.is_active === "1") return "active";
      if (drive?.is_active === "0") return "inactive";
      return "active";
    })(),
  };
  const stepValidationSchemas = {
    1: validationSchemaStep1,
    2: validationSchemaStep2,
    3: validationSchemaStep3,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    validationSchema: (() => {
      switch (step) {
        case 1:
          return validationSchemaStep1;
        case 2:
          return validationSchemaStep2;
        case 3:
          return validationSchemaStep3;
        default:
          return null;
      }
    })(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;
      if (step === 3) {
        const formData = new FormData();
        formData.append("college_id", drive?.college_id || collegeId);
        formData.append("company_name", values.companyName);

        if (values.jobPostName?.__isNew__) {
          formData.append("other_job_role_name", values.jobPostName?.label);
        } else {
          formData.append("job_role_id", values.jobPostName?.value);
        }
        formData.append("job_type", values.jobType);
        formData.append("salary", values.salaryStipend);
        formData.append("compensation_details", values.compensationDetails);
        formData.append("job_location", values.jobLocation);
        formData.append("total_vacancy", values.totalVacancy);
        formData.append("company_address", values.companyAddress);
        formData.append("hr_contact_person", values.companyHRName);
        formData.append("hr_email", values.companyHREmail);
        formData.append("hr_contact_number", values.companyHRContact);
        formData.append(
          "alternate_contact_number",
          values.alternateContactDetails
        );
        formData.append(
          "joining_date",
          moment(values.joiningDate).format("YYYY-MM-DD")
        );
        formData.append("apply_online_url", values.applyOnlineURL);
        formData.append("job_description", values.jobDescription);
        formData.append(
          "placement_eligibility_criteria",
          values.placementEligibilityCriteria
        );
        formData.append("placement_interview_rounds", values.interviewRounds);
        formData.append(
          "placement_terms_and_conditions",
          values.termsAndConditions
        );
        formData.append("course_id", values.selectedCourses.join(","));
        formData.append(
          "specialization_id",
          values.selectedSpecializations.join(",")
        );
        formData.append("passout_year", values.selectedPassoutYears.join(","));
        formData.append(
          "pre_placement_offer",
          values.prePlacementOffer ? "Yes" : "No"
        );

        if (values.prePlacementOffer) {
          formData.append(
            "ppo_eligibility_criteria",
            values.prePlacementOfferEligibilityCriteria
          );
        }
        formData.append(
          "job_description_file_name",
          values.job_description_file_name
        );
        formData.append(
          "additional_document_file_name",
          values.additional_document_file_name
        );

        if (drive?.campus_drive_id) {
          formData.append("campus_drive_id", drive?.campus_drive_id);
        }

        formData.append("is_active", values.status === "active" ? "1" : "0");
        const response = await axios.post(
          `${API_URL}/api/freshment_portal_apis/CampusDrive/create_campus_drive_job`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (response.data.success) {
          toast.success(response.data.message);
          history.push("/campus-drive-list");
          setIsFileUploaded(false);
          setIsAdditionalFileUploaded(false);
        } else {
          toast.error(
            <>{response.data.message || "Something went wrong"}</>
          );
        }
      } else {
        setStep(step + 1);
      }
    },
  });

  // Function to validate just the current step
  const validateStep = async (currentStep) => {
    try {
      const schema = stepValidationSchemas[currentStep];
      if (!schema) return true;

      const step1Fields = [
        "companyName",
        "jobPostName",
        "jobType",
        "salaryStipend",
        "compensationDetails",
        "jobLocation",
        "jobLocationId",
        "totalVacancy",
        "companyAddress",
        "companyHRContact",
        "companyHREmail",
        "companyHRName",
        "joiningDate",
        "applyOnlineURL",
        "prePlacementOffer",
        ...(formik.values.prePlacementOffer
          ? ["prePlacementOfferEligibilityCriteria"]
          : []),
        "alternateContactDetails",
      ];

      const step2Fields = [
        "jobDescription",
        "placementEligibilityCriteria",
        "interviewRounds",
        "termsAndConditions",
        "selectedCourses",
        "selectedSpecializations",
        "selectedPassoutYears",
      ];

      // Only add file validation if no existing files are present
      if (!formik.values.job_description_file_name) {
        step2Fields.push("files");
      }

      if (!formik.values.additional_document_file_name) {
        step2Fields.push("additionalFile");
      }

      // Always require the file name fields
      step2Fields.push(
        "job_description_file_name",
        "additional_document_file_name"
      );

      const step3Fields = ["status"];

      const currentFields =
        currentStep === 1
          ? step1Fields
          : currentStep === 2
            ? step2Fields
            : step3Fields;

      const currentValues = {};
      currentFields.forEach((field) => {
        currentValues[field] = formik.values[field];
      });

      await schema.validate(currentValues, { abortEarly: false });
      setStepErrors({});
      return true;
    } catch (err) {
      // Format Yup validation errors
      const errors = {};
      if (err.inner) {
        console.log(err.inner);
        err.inner.forEach((error) => {
          errors[error.path] = error.message;
        });
      }
      setStepErrors(errors);
      return false;
    }
  };

  // console.log(stepErrors);
  // console.log(formik.touched);

  const renderStep3 = (values) => (
    <div className="preview-form">
      {/* Job Details Section (from Step 1) */}
      <Row>
        <Col md={8}>
          <div className="section mb-4 p-4 bg-white rounded shadow-sm">
            <h4 className="mb-4 text-primary">Job Role Preview</h4>
            <div className="preview-content">
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-0 me-3">{values.jobPostName?.label}</h5>
                <span className="badge bg-primary">
                  {jobTypeOptions.find((opt) => opt.value === values.jobType)
                    ?.label || values.jobType}
                </span>
              </div>
              <div className="d-flex align-items-center text-muted mb-3">
                <i className="fas fa-map-marker-alt me-2"></i>
                <span>{values.jobLocation}</span>
                {values.salaryStipend && (
                  <>
                    <i className="fas fa-money-bill-wave mx-2"></i>
                    <span>
                      {values.jobType === "internship"
                        ? values.salaryStipend
                        : getSalaryLPA(values.salaryStipend)}
                      {values.jobType === "internship" ? " per month" : " LPA"}
                    </span>
                  </>
                )}
                <i className="fas fa-users mx-2"></i>
                <span>{values.totalVacancy} positions</span>
              </div>
              {values.jobType === "internship" && values.prePlacementOffer && (
                <div className="d-flex align-items-center text-muted mb-3">
                  <i className="fas fa-star me-2 text-warning"></i>
                  <span>Pre-placement offer available</span>
                </div>
              )}
              {values.selectedCourses.length > 0 && (
                <div className="d-flex align-items-center text-muted mb-3">
                  <i className="fas fa-graduation-cap me-2"></i>
                  <span>
                    {courses
                      .filter((course) =>
                        values.selectedCourses.includes(course.course_master_id)
                      )
                      .map((course, index, filteredCourses) => (
                        <span key={index}>
                          {course.name}
                          {index < filteredCourses.length - 1 ? ", " : ""}
                        </span>
                      ))}
                  </span>
                </div>
              )}
              {values.selectedSpecializations.length > 0 && (
                <div className="d-flex align-items-center text-muted">
                  <i className="fas fa-user-graduate me-2"></i>
                  <span>
                    {values.selectedSpecializations.length > 0 &&
                      specializations && (
                        <span>
                          {specializations
                            .filter((spec) =>
                              values.selectedSpecializations.includes(
                                spec.specilization_master_id
                              )
                            )
                            .map((spec) => spec.name)
                            .join(", ")}
                        </span>
                      )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="section p-4 bg-white rounded shadow-sm">
            <h4 className="mb-4 text-primary">Company Details</h4>
            <div className="preview-content">
              <div className="d-flex align-items-center text-muted mb-3">
                <i className="fas fa-building me-2"></i>
                <span>Company Name: {values.companyName}</span>
              </div>
              <div className="d-flex align-items-center text-muted mb-3">
                <i className="fas fa-map-marker-alt me-2"></i>
                <span>Company Address: {values.companyAddress}</span>
              </div>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="section mb-4 p-4 h-100  bg-white rounded shadow-sm">
            <h4 className="mb-4 text-primary">HR Details</h4>
            <Row>
              <Col md={12}>
                <div className="mb-3 d-flex align-items-center">
                  <i className="fa fa-user me-2 text-muted"></i>
                  <div className="text-muted">{values.companyHRName}</div>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3 d-flex align-items-center">
                  <i className="fa fa-envelope me-2 text-muted"></i>
                  <div className="text-muted">{values.companyHREmail}</div>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3 d-flex align-items-center">
                  <i className="fa fa-phone me-2 text-muted"></i>
                  <div className="text-muted">{values.companyHRContact}</div>
                </div>
              </Col>
              {values.alternateContactDetails && (
                <Col md={12}>
                  <div className="mb-3 d-flex align-items-center">
                    <i className="fa fa-phone me-2 text-muted"></i>
                    <div className="text-muted">
                      Alternate Contact: {values.alternateContactDetails}
                    </div>
                  </div>
                </Col>
              )}
              <Col md={12}>
                {values.applyOnlineURL && (
                  <div className="mb-3 d-flex align-items-center">
                    <i className="fa fa-link me-2 text-muted"></i>
                    <div className="text-muted">{values.applyOnlineURL}</div>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      {/* Requirements Section (from Step 2) */}
      <div className="section mb-4 mt-4 p-4 bg-white rounded shadow-sm">
        <h4 className="mb-4 text-primary">Requirements & Description</h4>

        <Row className="align-items-start">
          <Col md={6}>
            <div
              className="job-description p-3 h-100 border rounded mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <h5 className="mb-3 d-flex align-items-center">
                <i className="fa fa-info me-2 text-primary"></i>
                Job Description
              </h5>
              <div className="formatted-text">
                {values.jobDescription.split("\n").map((line, i) => (
                  <p key={i} className="mb-2">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </Col>

          <Col md={6}>
            <div
              className="job-requirements p-3 h-100 border rounded"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <h5 className="mb-3 d-flex align-items-center">
                <i className="fa fa-list-ul me-2 text-primary"></i>
                Requirements
              </h5>
              <div className="formatted-text">
                {values.placementEligibilityCriteria
                  .split("\n")
                  .map((line, i) => (
                    <p key={i} className="mb-2">
                      {/* <i className="fa fa-check-circle me-2 text-success"></i> */}
                      {line}
                    </p>
                  ))}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          {values.interviewRounds && (
            <Col md={12}>
              <div
                className="interview-rounds mt-3 p-3 border rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <h5 className="mb-3 d-flex align-items-center">
                  <i className="fa fa-handshake me-2 text-primary"></i>
                  Interview Process
                </h5>
                <div className="formatted-text">
                  {values.interviewRounds.split("\n").map(
                    (line, i) =>
                      line?.trim() && (
                        <p key={i} className="mb-2">
                          <i className="fa fa-arrow-right me-2 text-info"></i>
                          {line}
                        </p>
                      )
                  )}
                </div>
              </div>
            </Col>
          )}
        </Row>

        {values.prePlacementOffer && (
          <Row>
            <Col md={12}>
              <div
                className="interview-rounds mt-3 p-3 border rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <h5 className="mb-3 d-flex align-items-center">
                  <i className="fa fa-chart-line me-2 text-primary"></i>
                  Pre-Placement Offer Eligibility Criteria
                </h5>
                <div className="formatted-text">
                  <p>{values.prePlacementOfferEligibilityCriteria}</p>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </div>

      {/* Files Section - Simplified */}
      {(values.job_description_file_name ||
        values.additional_document_file_name) && (
          <div className="section mb-4 p-4 bg-white rounded shadow-sm">
            <h4 className="mb-4 text-primary d-flex align-items-center">
              <i className="fa fa-paperclip me-2"></i>
              Uploaded Files
            </h4>

            <Row>
              {values.job_description_file_name && (
                <Col md={6}>
                  <div
                    className="d-flex align-items-center p-2 border rounded mb-2"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <i
                      className={`fa fa-${values.job_description_file_name
                          .toLowerCase()
                          .endsWith(".pdf")
                          ? "file-pdf-o text-danger"
                          : "file-word-o text-primary"
                        } me-2`}
                    ></i>
                    <div className="flex-grow-1 ms-2 overflow-hidden">
                      <div className="text-truncate">
                        {values.job_description_file_name}
                      </div>
                    </div>
                  </div>
                </Col>
              )}
              {values.additional_document_file_name && (
                <Col md={6}>
                  <div
                    className="d-flex align-items-center p-2 border rounded mb-2"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <i
                      className={`fa fa-${values.additional_document_file_name
                          .toLowerCase()
                          .endsWith(".pdf")
                          ? "file-pdf-o text-danger"
                          : "file-word-o text-primary"
                        } me-2`}
                    ></i>
                    <div className="flex-grow-1 ms-2 overflow-hidden">
                      <div className="text-truncate">
                        {values.additional_document_file_name}
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}

      {/* Status Section - Simplified */}
      <div className="section mb-4 p-4 bg-white rounded shadow-sm">
        <h4 className="mb-4 text-primary">Active/Inactive</h4>

        <div className="form-group">
          <div className="d-flex">
            <div className="form-check me-4">
              <input
                type="radio"
                name="status"
                value="active"
                className="form-check-input"
                id="status-active"
                checked={values.status === "active"}
                onChange={formik.handleChange}
              />
              <label className="form-check-label" htmlFor="status-active">
                Active
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                name="status"
                value="inactive"
                className="form-check-input"
                id="status-inactive"
                checked={values.status === "inactive"}
                onChange={formik.handleChange}
              />
              <label className="form-check-label" htmlFor="status-inactive">
                Inactive
              </label>
            </div>
          </div>
          {formik.errors.status && formik.touched.status && (
            <div className="text-danger mt-1">{formik.errors.status}</div>
          )}
        </div>
      </div>
    </div>
  );

  const prevStep = () => {
    setStep(step - 1);
    setStepErrors({});
  };

  const handleNext = async (e) => {
    e.preventDefault();
    const step1Fields = [
      "companyName",
      "jobPostName",
      "jobType",
      "salaryStipend",
      "compensationDetails",
      "jobLocation",
      "jobLocationId",
      "totalVacancy",
      "companyAddress",
      "companyHRContact",
      "companyHREmail",
      "companyHRName",
      "joiningDate",
      "applyOnlineURL",
      "prePlacementOffer",
      ...(formik.values.prePlacementOffer
        ? ["prePlacementOfferEligibilityCriteria"]
        : []),
      "alternateContactDetails",
    ];

    const step2Fields = [
      "jobDescription",
      "placementEligibilityCriteria",
      "interviewRounds",
      "termsAndConditions",
      "selectedCourses",
      "selectedSpecializations",
      "selectedPassoutYears",
      "files",
      "additionalFile",
    ];

    const step3Fields = ["status"];

    // Get fields for current step
    const currentFields =
      step === 1 ? step1Fields : step === 2 ? step2Fields : step3Fields;

    // Mark fields as touched
    const touchedFields = {};
    currentFields.forEach((field) => {
      touchedFields[field] = true;
    });

    await formik.setTouched(touchedFields, true);

    const isValid = await validateStep(step);

    // Check for files that need to be uploaded in step 2
    if (step === 2) {
      if (formik.values.files && !isFileUploaded) {
        toast.error(
          <>
            Please upload the job description file<br />
            You have selected a file but haven't uploaded it yet.
          </>
        );
        return false;
      }

      if (formik.values.additionalFile && !isAdditionalFileUploaded) {
        toast.error(
          <>
            Please upload the additional document file<br />
            You have selected a file but haven't uploaded it yet.
          </>
        );
        return false;
      }
    }

    if (!isValid) {
      toast.error(
        <>
          Validation Error<br />
          Please fill in all required fields correctly before proceeding.
        </>
      );
      return false;
    }

    // If this is the final step, submit the form
    if (step === 3) {
      formik.handleSubmit(e);
    } else {
      setStep(step + 1);
      setStepErrors({});
    }
  };

  return (
    <div className="form-container py-4">
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <h2 className="fw-bold mb-5">
            {drive ? "Edit Campus Drive" : "Schedule a Campus Drive"}
          </h2>

          {/* Step Indicators */}
          <div className="steps-container mb-4">
            <div className="d-flex justify-content-center mb-4">
              <div className="step-progress mb-5 px-5 w-100">
                <div className="d-flex justify-content-between position-relative align-items-center">
                  <div className={`step-circle ${step >= 1 ? "active" : ""}`}>
                    <span>1</span>
                    <div className="step-label">Basic Details</div>
                  </div>
                  <div className={`step-circle ${step >= 2 ? "active" : ""}`}>
                    <span>2</span>
                    <div className="step-label">Requirements</div>
                  </div>
                  <div className={`step-circle ${step === 3 ? "active" : ""}`}>
                    <span>3</span>
                    <div className="step-label">Preview</div>
                  </div>
                  <div className="step-connector"></div>
                  <div
                    className="step-progress-bar"
                    style={{ width: `${(step - 1) * 50}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {step === 1 && <JobDetailsForm formik={formik} />}

            {step === 2 && (
              <RequirementForm
                formik={formik}
                onSpecializationsChange={setSpecializations}
                setIsFileUploaded={setIsFileUploaded}
                setIsAdditionalFileUploaded={setIsAdditionalFileUploaded}
                drive={drive}
              />
            )}

            {step === 3 && renderStep3(formik.values)}

            <div className="mt-4 pt-3 d-flex border-top">
              <div>
                {step > 1 && (
                  <Button
                    variant="secondary"
                    className="me-2 px-4"
                    onClick={prevStep}
                    type="button"
                  >
                    Previous
                  </Button>
                )}
              </div>
              <div className="ms-auto">
                {step === 3 ? (
                  <Button
                    disabled={formik.isSubmitting}
                    variant="success"
                    type="submit"
                    className="px-4"
                    onClick={handleNext}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                ) : (
                  <Button
                    disabled={formik.isSubmitting}
                    onClick={handleNext}
                    variant="primary"
                    type="button"
                    className="px-4"
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CampusDriveForm;
